import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, Loader2, Play, Settings2 } from 'lucide-react';
import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { innerSurface } from '@/components/surface/tones';
import { Panel, SectionHeader } from '@/components/surface/ui';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { AUTOMATION_CATALOG_KEY, RUNS_KEY, automationApi, type StartRunInput } from '@/lib/automation-api';
import { getErrorMessage, getFieldErrors } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Field, Toggle } from '@/modules/automation/ui';
import type { RunMode, RunStrategy } from '@/types/api';

/**
 * How a run proceeds, and the buttons that start it.
 *
 * ## Safe by default
 *
 * A canary goes first, the rest wait for a person to confirm, they go five at a time, and the run
 * stops at the first failure. Every one of those can be changed, and each says what changing it
 * means — but the form a person does not touch is the careful one.
 */
export function LaunchPanel({
  strategy,
  onStrategyChange,
  serverCount,
  allowDryRun,
  build,
  onFieldErrors,
  blockedReason,
  runLabel = 'Run',
}: {
  strategy: RunStrategy;
  onStrategyChange: (strategy: RunStrategy) => void;
  /** Servers the selection reaches, once the preview knows; null before. */
  serverCount: number | null;
  allowDryRun: boolean;
  /** Everything but the mode, which the button pressed decides. */
  build: (mode: RunMode) => StartRunInput;
  onFieldErrors: (errors: Record<string, string>) => void;
  /** Why nothing can be started yet, in words; null when it can. */
  blockedReason: string | null;
  runLabel?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permitsChanges } = useSubscriptionAccess();
  const batchId = useId();
  const failuresId = useId();

  const start = useMutation({
    mutationFn: (mode: RunMode) => automationApi.startRun(build(mode)),
    onMutate: () => onFieldErrors({}),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: RUNS_KEY });
      // The catalogue carries what is left of a counted Command Runner allowance, and this run
      // just used one.
      if (run.kind === 'command') {
        void queryClient.invalidateQueries({ queryKey: AUTOMATION_CATALOG_KEY });
      }
      navigate(`/automation/runs/${run.id}`);
    },
    onError: (error) => onFieldErrors(getFieldErrors(error)),
  });

  const fieldErrors = getFieldErrors(start.error);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const canaryApplies = serverCount === null || serverCount > 1;
  const disabled = !permitsChanges || blockedReason !== null || start.isPending;

  const set = (patch: Partial<RunStrategy>) => onStrategyChange({ ...strategy, ...patch });

  return (
    <Panel>
      <SectionHeader icon={Settings2} iconTone="primary" title="How it runs" description="Every run checks each server before changing anything." />

      <div className="space-y-5 px-5 pb-5">
        <Toggle
          id={`${batchId}-canary`}
          checked={strategy.canary && canaryApplies}
          disabled={!canaryApplies}
          onChange={(canary) => set({ canary })}
          label="Try one server first"
          description={
            canaryApplies
              ? 'The first server that passes the preflight runs alone. Nothing else starts until you have checked its result and confirmed.'
              : 'There is only one server, so there is nothing to try it on first.'
          }
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="At a time" htmlFor={batchId} help="How many servers change at once." error={fieldErrors['strategy.batch_size']}>
            <Input
              id={batchId}
              type="number"
              min={1}
              max={50}
              value={strategy.batch_size}
              onChange={(event) => set({ batch_size: Math.max(1, Math.min(50, Number(event.target.value) || 1)) })}
            />
          </Field>
          <Field
            label="Stop after"
            htmlFor={failuresId}
            help={strategy.max_failures === 0 ? 'The first failure stops further batches.' : `More than ${strategy.max_failures} failure${strategy.max_failures === 1 ? '' : 's'} stops further batches.`}
            error={fieldErrors['strategy.max_failures']}
          >
            <Input
              id={failuresId}
              type="number"
              min={0}
              max={1000}
              value={strategy.max_failures}
              onChange={(event) => set({ max_failures: Math.max(0, Math.min(1000, Number(event.target.value) || 0)) })}
            />
          </Field>
        </div>

        <ol className={cn(innerSurface, 'space-y-1 px-4 py-3 text-[13px] text-muted-foreground')}>
          <li>
            <span className="font-medium text-foreground">1.</span> Preflight {serverCount === null ? 'every server' : `${serverCount} server${serverCount === 1 ? '' : 's'}`}: connect,
            detect the system, check root access. Nothing is changed.
          </li>
          {strategy.canary && canaryApplies && (
            <li>
              <span className="font-medium text-foreground">2.</span> Run on one server, then wait for your confirmation.
            </li>
          )}
          <li>
            <span className="font-medium text-foreground">{strategy.canary && canaryApplies ? '3.' : '2.'}</span> The rest, {strategy.batch_size} at a time
            {strategy.max_failures === 0 ? ', stopping at the first failure.' : `, stopping after ${strategy.max_failures + 1} failures.`}
          </li>
        </ol>

        {start.isError && !hasFieldErrors && (
          <p className="rounded-lg bg-danger/[0.06] px-3 py-2 text-sm dark:bg-danger/[0.08]" role="alert">
            {getErrorMessage(start.error)}
          </p>
        )}
        {hasFieldErrors && (
          <p className="text-sm text-danger" role="alert">
            Some choices need attention: {Object.values(fieldErrors)[0]}
          </p>
        )}
        {blockedReason !== null && <p className="text-sm text-muted-foreground">{blockedReason}</p>}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {allowDryRun && (
            <Button variant="outline" disabled={disabled} onClick={() => start.mutate('dry_run')}>
              {start.isPending && start.variables === 'dry_run' ? <Loader2 className="animate-spin" /> : <FlaskConical />}
              Dry run
            </Button>
          )}
          <Button className="!shadow-none" disabled={disabled} onClick={() => start.mutate('apply')}>
            {start.isPending && start.variables === 'apply' ? <Loader2 className="animate-spin" /> : <Play />}
            {runLabel}
            {serverCount !== null && serverCount > 0 && ` on ${serverCount} server${serverCount === 1 ? '' : 's'}`}
          </Button>
        </div>
        {allowDryRun && (
          <p className="text-right text-xs text-muted-foreground">A dry run reports what would change on every server and changes nothing.</p>
        )}
      </div>
    </Panel>
  );
}
